import React, { useEffect, useState } from 'react';
import {
	ColorPickerField,
	EditTabField,
	SwitchField,
	TextField,
} from '@extensions/components/Form';
import { Align } from '@extensions/AttributePanel/components/attributes/Align';
import { Collapse, Grid } from '@arco-design/web-react';
import { NavbarLinkPadding } from '@extensions/AttributePanel/components/attributes/NavbarLinkPadding';
import {
	useFocusIdx,
	getShadowRoot,
	DATA_CONTENT_EDITABLE_IDX,
	useEditorContext,
} from '@bay-email/editor';
import { INavbar, t } from '@bay-email/core';
import { ClassName } from '../../attributes/ClassName';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import {
	FontFamily,
	FontStyle,
	FontWeight,
	LetterSpacing,
	LineHeight,
	TextDecoration,
	TextTransform,
} from '../../attributes';
import { pixelAdapter } from '../../adapter';
import { usePageDefaults } from '@extensions/hooks/usePageDefaults';
import { useSelectedNode } from '@extensions/hooks/useSelectedNode';

export function Navbar() {
	const { focusIdx } = useFocusIdx();
	const { formHelpers } = useEditorContext();
	const { selectedNode } = useSelectedNode();
	const [activeLinksTab, setActiveLinksTab] = useState('0');

	useEffect(() => {
		const links = (selectedNode as any)?.data?.value?.links;
		if (!Array.isArray(links) || links.length === 0) return;

		// Navbar links should default to black text with white background,
		// so they do not inherit color styles from parent/page blocks.
		links.forEach((link, index) => {
			if (!link?.color) {
				formHelpers.change(`${focusIdx}.data.value.links[${index}].color`, '#000000');
			}

			if (!link?.['background-color']) {
				formHelpers.change(
					`${focusIdx}.data.value.links[${index}]['background-color']`,
					'#FFFFFF',
				);
			}
		});
	}, [focusIdx, formHelpers, selectedNode]);

	useEffect(() => {
		const shadowRoot = getShadowRoot();
		if (!shadowRoot) return;

		const syncTabFromElement = (el: Element | null) => {
			if (!(el instanceof Element)) return;
			const editableIdx = el.getAttribute(DATA_CONTENT_EDITABLE_IDX);
			if (!editableIdx) return;
			const prefix = `${focusIdx}.data.value.links.`;
			if (!editableIdx.startsWith(prefix)) return;
			const match = /^(\d+)/.exec(editableIdx.slice(prefix.length));
			if (match) {
				setActiveLinksTab(match[1]);
			}
		};

		// On mount / focusIdx change: the user may have clicked a link to re-select
		// this navbar, causing `focusin` to fire before this effect registered.
		// `shadowRoot.activeElement` is already set at this point, so sync from it.
		syncTabFromElement(shadowRoot.activeElement);

		const onFocusin = (e: FocusEvent) => syncTabFromElement(e.target as Element);

		shadowRoot.addEventListener('focusin', onFocusin as EventListener);
		return () => shadowRoot.removeEventListener('focusin', onFocusin as EventListener);
	}, [focusIdx]);

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={['0', '1']}
			style={{ padding: 0 }}
		>
			<Collapse.Item
				name='0'
				header={t('Layout')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<Align />
				</div>
			</Collapse.Item>

			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='1'
				header={t('Navbar Links')}
			>
				<EditTabField
					tabPosition='top'
					name={`${focusIdx}.data.value.links`}
					label={t('Links')}
					labelHidden
					activeTab={activeLinksTab}
					onActiveTabChange={setActiveLinksTab}
					renderItem={(item, index) => (
						<NavbarLink
							item={item}
							index={index}
						/>
					)}
					additionItem={{
						href: 'https://www.mailjet.com/wp-content/uploads/2016/11/ecommerce-guide.jpg',
						target: '_blank',
						content: 'New link',
						padding: '15px 10px 15px 10px',
						color: '#000000',
						'background-color': '#FFFFFF',
						'text-transform': 'uppercase',
					}}
					formItem={{
						className: 'mb-0',
					}}
				/>
			</Collapse.Item>

			<Collapse.Item
				name='2'
				header={t('Extra')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<TextField
						label={t('Base URL')}
						name={`${focusIdx}.attributes.base-url`}
						autoComplete='off'
					/>
					<ClassName />
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}

function NavbarLink({
	item,
	index,
}: {
	item: INavbar['data']['value']['links'];
	index: number;
}) {
	const { focusIdx } = useFocusIdx();
	const { fontSize } = usePageDefaults();
	return (
		<div className='NavbarLink p-3'>
			<TextField
				label={t('Text')}
				name={`${focusIdx}.data.value.links.[${index}].content`}
			/>
			<ColorPickerField
				label={t('Text Color')}
				name={`${focusIdx}.data.value.links.[${index}].color`}
			/>
			<ColorPickerField
				label={t('Background Color')}
				name={`${focusIdx}.data.value.links.[${index}].background-color`}
			/>
			<FontFamily name={`${focusIdx}.data.value.links.[${index}].font-family`} />
			<Grid.Row gutter={16}>
				<Grid.Col span={12}>
					<div style={{ paddingRight: '8px' }}>
						<TextField
							label={t('Font size (px)')}
							name={`${focusIdx}.data.value.links.[${index}].font-size`}
							config={pixelAdapter}
							autoComplete='off'
							placeholder={fontSize}
						/>
					</div>
				</Grid.Col>
				<Grid.Col span={12}>
					<div style={{ paddingLeft: '8px' }}>
						<LetterSpacing
							label={t('Letter spacing (px)')}
							name={`${focusIdx}.data.value.links.[${index}].letter-spacing`}
							config={pixelAdapter}
						/>
					</div>
				</Grid.Col>
			</Grid.Row>
			<LineHeight name={`${focusIdx}.data.value.links.[${index}].line-height`} />
			<FontWeight name={`${focusIdx}.data.value.links.[${index}].font-weight`} />
			<FontStyle name={`${focusIdx}.data.value.links.[${index}].font-style`} />
			<TextDecoration name={`${focusIdx}.data.value.links.[${index}].text-decoration`} />
			<TextTransform name={`${focusIdx}.data.value.links.[${index}].text-transform`} />
			<TextField
				label={t('Link Address')}
				placeholder=''
				name={`${focusIdx}.data.value.links.[${index}].href`}
			/>
			<SwitchField
				label={<span className='fw-normal'>{t('Open in a new tab')}</span>}
				name={`${focusIdx}.data.value.links.[${index}].target`}
				size='small'
				config={{
					format: (value: string) => value === '_blank',
					parse: (value: boolean) => (value ? '_blank' : '_self'),
				}}
				formItem={{
					style: {
						display: 'flex',
						alignItems: 'center',
						marginBottom: '0px',
					},
					labelCol: {
						span: 0,
						style: { order: 2, paddingLeft: '12px' },
					},
					wrapperCol: {
						span: 0,
						style: { order: 1 },
					},
				}}
			/>
			<NavbarLinkPadding
				key={index}
				name={`${focusIdx}.data.value.links.[${index}].padding`}
			/>
		</div>
	);
}
